<template>
    <modal
        show
        dismissable
        body-classes="p-0"
        modal-classes="modal-xl"
        @update:show="$emit('close')"
    >
        <card class="card-form">
            <template slot="header">
                <h2 class="modal-title">Select Receipt Logs</h2>
                <button
                    type="button"
                    class="close"
                    aria-label="Close"
                    @click="$emit('close')"
                >
                    <i class="tim-icons icon-simple-remove" />
                </button>
            </template>
            <p>
                Click on one or more receipt logs below to assign them to your
                future delivery request.
            </p>
            <div class="flex-between" :class="$style.actionBar">
                <base-input>
                    <el-input
                        v-model="searchQuery"
                        type="search"
                        class="mb-3 search-input"
                        prefix-icon="el-icon-search"
                        placeholder="Search records"
                    />
                </base-input>
                <base-button
                    :disabled="selection.length === 0"
                    type="primary"
                    class="btn-fill"
                    @click="confirmSelection"
                >
                    Assign to {{ selection.length }} receipt log{{
                        selection.length === 1 ? "" : "s"
                    }}
                </base-button>
            </div>
            <el-table
                v-sticky-theader
                :data="receiptLogs"
                @selection-change="handleSelectionChange"
            >
                <template slot="empty"> No receipt logs found. </template>
                <el-table-column type="selection" />
                <el-table-column
                    v-for="column in tableColumns"
                    :key="column.label"
                    :width="column.width"
                    :min-width="column.minWidth"
                    :prop="column.prop"
                    :label="column.label"
                    :sortable="column.sortable"
                    :sort-method="column.sortMethod"
                    :show-overflow-tooltip="true"
                >
                    <template slot-scope="scope">
                        <template v-if="scope.column.property === 'id'">
                            <i
                                v-if="scope.row.notes"
                                class="far fa-sticky-note has-notes-icon"
                                title="This item has notes"
                            />
                            <span v-else class="no-notes-spacer" />
                            {{ scope.row.id }}
                        </template>
                        <template v-else-if="scope.column.property === 'date'">
                            {{ formatDate(scope.row.date) }}
                        </template>
                        <template
                            v-else-if="scope.column.property === 'materialType'"
                        >
                            {{
                                `${scope.row.materialType.description} ${scope.row.size} ${scope.row.sizeType}`
                            }}
                        </template>
                        <template v-else>
                            {{ getProp(scope.row, scope.column.property) }}
                        </template>
                    </template>
                </el-table-column>
            </el-table>
        </card>
    </modal>
</template>

<script>
import swal from "sweetalert2";
import _ from "lodash";
import { mapActions } from "vuex";
import moment from "moment-timezone";
import { Modal } from "src/components/UI";

export default {
    name: "ReceiptLogSelectModal",
    components: { Modal },
    props: {
        excludeColumns: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            receiptLogs: [],
            searchQuery: "",
            selection: [],
        };
    },
    computed: {
        tableColumns() {
            let columns = [
                {
                    prop: "id",
                    label: "Receipt No.",
                    sortable: true,
                },
                {
                    prop: "customer.name",
                    label: "Customer",
                    sortable: true,
                },
                {
                    prop: "designer.fullName",
                    label: "Designer",
                    sortable: true,
                },
                {
                    prop: "date",
                    label: "Received",
                    sortable: true,
                    sortMethod: (a, b) => new Date(b.date) - new Date(a.date),
                },
                {
                    prop: "manufacturer.name",
                    label: "Manufacturer",
                    sortable: true,
                },
                {
                    prop: "jobOrderNumber",
                    label: "Job Order No.",
                    sortable: true,
                },
                {
                    prop: "purchaseOrder",
                    label: "PO",
                    sortable: true,
                },
                {
                    prop: "customerJobId",
                    label: "Job Id",
                    sortable: true,
                },
                {
                    prop: "materialType",
                    label: "Materials Rcvd",
                    sortable: true,
                },
            ];
            // eslint-disable-next-line max-len
            columns = columns.filter(
                (column) =>
                    this.excludeColumns.findIndex(
                        (hide) => hide === column.label,
                    ) === -1,
            );
            return columns;
        },
    },
    watch: {
        searchQuery: {
            immediate: true,
            handler: _.debounce(
                async function updated(query) {
                    this.receiptLogs = await this.fetchReceiptLogs({
                        search: query,
                    });
                },
                500,
                { trailing: true },
            ),
        },
    },
    methods: {
        ...mapActions("$_futureDeliveryRequest", ["fetchReceiptLogs"]),
        async confirmSelection() {
            const { value } = await swal({
                title: "Confirm selection",
                allowEscapeKey: false,
                allowOutsideClick: false,
                showConfirmButton: true,
                reverseButtons: true,
                confirmButtonText: "Yes",
                confirmButtonColor: "#3085d6",
                showCancelButton: true,
                cancelButtonText: "Cancel",
                cancelButtonColor: "#ff8d72",
                type: "question",
                html: `Are you sure you want to assign this future request to the receipt logs listed below?<br>
        <strong style="font-weight: 600;">${this.selection.map((log) => log.id).join(", ")}</strong>`,
            });
            if (value) {
                this.$emit("receipt-logs-selected", this.selection);
            }
        },
        handleSelectionChange(selection) {
            this.selection = selection;
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "--";
        },
        getProp(object, property) {
            return _.get(object, property);
        },
    },
};
</script>

<style module>
.actionBar {
    height: 48px;
}
</style>
